import { useEffect, useState, type ReactNode } from 'react';
import { Gift, MapPin, Settings2, ShieldCheck, Plus, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@/shared/components/page-header';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { DatePicker } from '@/shared/ui/date-picker';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';
import { fetchCampaignStores, type StoreOption } from '@/features/campaigns/api/use-campaigns';
import { CampaignStorePicker } from './campaign-store-picker';
import {
  BENEFIT_KINDS,
  PAYMENT_TYPES,
  SALE_CATEGORIES,
  saveLoyaltyCampaign,
  useLoyaltyCampaigns,
  type BenefitKind,
} from '../api/use-loyalty-admin';
import {
  addBenefit,
  campaignToForm,
  DISCOUNT_MODES,
  emptyForm,
  formToPayload,
  removeBenefit,
  toggleValue,
  updateBenefit,
  validateBasics,
  validateBenefits,
  type DiscountMode,
  type FormState,
} from '../model/campaign-form';

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="block">{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function ToggleGroup({
  options,
  selected,
  onToggle,
}: {
  options: readonly { value: number; label: string }[];
  selected: number[];
  onToggle: (value: number) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Button
          key={o.value}
          type="button"
          size="sm"
          variant={selected.includes(o.value) ? 'primary' : 'outline'}
          onClick={() => onToggle(o.value)}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Cadastro de campanha em stepper — cada etapa passa a valer quando o motor
 * já resolveu o desconto sozinho (o PDV só aplica o que a etapa Benefícios
 * calculou), então um campo errado aqui é preço errado no cupom.
 */
export function LoyaltyCampaignFormPage() {
  const { idPassbook, campaignId } = useParams<{ idPassbook: string; campaignId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(campaignId);
  const listPath = `/loja/${idPassbook}/campanhas/fidelidade`;

  const { data: campaigns = [], isLoading } = useLoyaltyCampaigns(idPassbook);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loadedForEdit, setLoadedForEdit] = useState(false);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idPassbook) void fetchCampaignStores(idPassbook).then(setStores);
  }, [idPassbook]);

  useEffect(() => {
    if (!isEdit || loadedForEdit || isLoading) return;
    const found = campaigns.find((c) => String(c.campaignId) === campaignId);
    if (found) {
      setForm(campaignToForm(found));
      setLoadedForEdit(true);
    } else {
      toast.error('Campanha não encontrada.');
      navigate(listPath);
    }
  }, [isEdit, loadedForEdit, isLoading, campaigns, campaignId, navigate, listPath]);

  const patch = (values: Partial<FormState>) => setForm((prev) => ({ ...prev, ...values }));

  const save = async () => {
    if (!idPassbook) return;
    setSaving(true);
    try {
      await saveLoyaltyCampaign(idPassbook, formToPayload(form));
      toast.success(isEdit ? 'Campanha atualizada.' : 'Campanha criada.');
      await queryClient.invalidateQueries({ queryKey: ['loyalty-campaigns', idPassbook] });
      navigate(listPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao salvar campanha.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const waitingEditData = isEdit && !loadedForEdit;

  const steps: WizardStep[] = [
    {
      id: 'dados',
      title: 'Dados',
      icon: Settings2,
      validate: () => {
        const error = validateBasics(form);
        if (error) {
          toast.error(error);
          return false;
        }
        return true;
      },
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <p className="text-sm font-medium">Ativa</p>
              <p className="text-xs text-muted-foreground">
                Participa da vitrine e aplica desconto no caixa.
              </p>
            </div>
            <Switch checked={form.active} onCheckedChange={(v) => patch({ active: v })} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Código" hint="É o identificador que volta no orçamento para o caixa.">
              <Input
                value={form.campaignId}
                onChange={(e) => patch({ campaignId: e.target.value })}
                disabled={isEdit}
                inputMode="numeric"
                placeholder="388"
              />
            </Field>
            <Field label="Nome">
              <Input value={form.name} onChange={(e) => patch({ name: e.target.value })} />
            </Field>
            <Field label="Categoria">
              <Input
                value={form.categoryName}
                onChange={(e) => patch({ categoryName: e.target.value })}
                placeholder="Pra acompanhar"
              />
            </Field>
            <Field label="Descrição">
              <Input
                value={form.descriptionCampaign}
                onChange={(e) => patch({ descriptionCampaign: e.target.value })}
                placeholder="1 Suco 300 ml (Limão)"
              />
            </Field>
            <Field label="Início da vigência">
              <DatePicker value={form.startDate} onChange={(v) => patch({ startDate: v })} />
            </Field>
            <Field label="Vigência até" hint="Vazio = sem data de término.">
              <DatePicker value={form.endDate} onChange={(v) => patch({ endDate: v })} />
            </Field>
          </div>
          <Field label="Descrição comercial" hint="Texto mostrado pro cliente na vitrine do app.">
            <Textarea
              value={form.descriptionCommercial}
              onChange={(e) => patch({ descriptionCommercial: e.target.value })}
              rows={2}
            />
          </Field>
          <Field label="Imagem" hint="URL da imagem exibida na vitrine — upload direto ainda não existe.">
            <Input
              value={form.image}
              onChange={(e) => patch({ image: e.target.value })}
              placeholder="https://…"
            />
          </Field>
        </div>
      ),
    },
    {
      id: 'beneficios',
      title: 'Benefícios',
      icon: Gift,
      validate: () => {
        const error = validateBenefits(form.benefits);
        if (error) {
          toast.error(error);
          return false;
        }
        return true;
      },
      content: (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs text-muted-foreground">
              O produto tem de estar no carrinho para a campanha valer. Sem benefício, a campanha
              nunca aplica desconto.
            </p>
            <div className="space-y-2">
              {form.benefits.map((b, i) => (
                <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_100px_100px_auto]">
                  <Input
                    value={b.productCode}
                    onChange={(e) =>
                      patch({ benefits: updateBenefit(form.benefits, i, { productCode: e.target.value }) })
                    }
                    placeholder="código do produto"
                  />
                  <Select
                    value={b.kind}
                    onValueChange={(v) =>
                      patch({ benefits: updateBenefit(form.benefits, i, { kind: v as BenefitKind }) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BENEFIT_KINDS.map((k) => (
                        <SelectItem key={k.value} value={k.value}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={b.value}
                    onChange={(e) =>
                      patch({ benefits: updateBenefit(form.benefits, i, { value: e.target.value }) })
                    }
                    inputMode="decimal"
                    placeholder="valor"
                  />
                  <Input
                    value={b.maxQuantity}
                    onChange={(e) =>
                      patch({ benefits: updateBenefit(form.benefits, i, { maxQuantity: e.target.value }) })
                    }
                    inputMode="numeric"
                    placeholder="máx un."
                  />
                  <ActionIconButton
                    label="Remover benefício"
                    onClick={() => patch({ benefits: removeBenefit(form.benefits, i) })}
                  >
                    <Trash2 className="size-4" />
                  </ActionIconButton>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => patch({ benefits: addBenefit(form.benefits) })}
            >
              <Plus className="size-4" /> Benefício
            </Button>
          </div>

          <Field label="Onde o desconto aparece no cupom">
            <RadioGroup
              value={form.discountMode}
              onValueChange={(v) => patch({ discountMode: v as DiscountMode })}
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              {DISCOUNT_MODES.map((m) => (
                <div key={m.value} className="flex items-center gap-2">
                  <RadioGroupItem value={m.value} id={`discount-${m.value}`} />
                  <Label htmlFor={`discount-${m.value}`} className="cursor-pointer font-normal">
                    {m.label} <span className="text-xs text-muted-foreground">— {m.hint}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </Field>
        </div>
      ),
    },
    {
      id: 'alcance',
      title: 'Alcance',
      icon: MapPin,
      content: (
        <div className="space-y-4">
          <Field label="Canais" hint="Nenhum marcado = vale em todos.">
            <ToggleGroup
              options={SALE_CATEGORIES}
              selected={form.channels}
              onToggle={(v) => patch({ channels: toggleValue(form.channels, v) })}
            />
          </Field>
          <Field label="Meios de pagamento" hint="Nenhum marcado = vale em todos.">
            <ToggleGroup
              options={PAYMENT_TYPES}
              selected={form.paymentTypes}
              onToggle={(v) => patch({ paymentTypes: toggleValue(form.paymentTypes, v) })}
            />
          </Field>
          <Field label="Lojas" hint="Nenhuma marcada = vale em todas. Com estado cadastrado, a lista agrupa por UF.">
            {stores.length > 0 ? (
              <CampaignStorePicker
                stores={stores}
                selected={form.storeCodes}
                onChange={(storeCodes) => patch({ storeCodes })}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma loja cadastrada para esta unidade.</p>
            )}
          </Field>
        </div>
      ),
    },
    {
      id: 'regras',
      title: 'Regras',
      icon: ShieldCheck,
      content: (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Grupo de conflito"
              hint="Campanhas do mesmo grupo se excluem: vence a de maior desconto e, empatando, a de menor ordem. 0 = não conflita."
            >
              <Input
                value={form.idConflict}
                onChange={(e) => patch({ idConflict: e.target.value })}
                inputMode="numeric"
              />
            </Field>
            <Field label="Ordem">
              <Input value={form.order} onChange={(e) => patch({ order: e.target.value })} inputMode="numeric" />
            </Field>
            <Field label="Promocode" hint="Vazio = não exige código.">
              <Input value={form.promocode} onChange={(e) => patch({ promocode: e.target.value })} />
            </Field>
            <Field label="Limite por CPF" hint="Vazio = sem teto.">
              <Input
                value={form.usageLimitPerCustomer}
                onChange={(e) => patch({ usageLimitPerCustomer: e.target.value })}
                inputMode="numeric"
              />
            </Field>
          </div>

          <div className="space-y-3">
            {(
              [
                {
                  key: 'requiresEnrollment',
                  label: 'Exige "Ativar" no app',
                  hint: 'sem a ativação do cliente, a campanha não vale no caixa',
                },
                {
                  key: 'isExclusive',
                  label: 'Exclusiva de CPF',
                  hint: 'só aparece na vitrine de quem tem a ativação',
                },
                {
                  key: 'cumulative',
                  label: 'Acumula com outra do mesmo grupo',
                  hint: 'escapa da regra de conflito',
                },
                { key: 'isGift', label: 'É brinde', hint: 'volta em campaignsGift' },
              ] as const
            ).map((item) => (
              <div key={item.key} className="flex items-start justify-between gap-3 rounded-xl border border-border p-3">
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.hint}</p>
                </div>
                <Switch
                  checked={form[item.key]}
                  onCheckedChange={(v) => patch({ [item.key]: v } as Partial<FormState>)}
                />
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  if (waitingEditData) return <Skeleton className="h-96 rounded-2xl" />;

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar campanha' : 'Nova campanha'}
        description="O motor calcula o desconto e devolve pronto para o caixa. O que estiver errado aqui vira preço errado no cupom."
      />
      <Card className="w-full min-w-0 p-6 sm:p-8">
        <Wizard
          steps={steps}
          compactStepper
          clickableStepper
          mobileOptimized
          onFinish={save}
          onCancel={() => navigate(listPath)}
          isSubmitting={saving}
          finishLabel={isEdit ? 'Salvar' : 'Criar campanha'}
        />
      </Card>
    </>
  );
}
